<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import {
  adjustAdminUserCredits,
  deleteAdminUser,
  listAdminUsers,
  resetAdminUserPassword,
  setAdminUserStatus,
  type AdminUser
} from "../admin-api";

const users = ref<AdminUser[]>([]);
const query = ref("");
const loading = ref(false);
const detailUser = ref<AdminUser | null>(null);
const passwordUser = ref<AdminUser | null>(null);
const creditUser = ref<AdminUser | null>(null);
const resetting = ref(false);
const adjustingCredits = ref(false);
const togglingUserId = ref("");
const deletingUserId = ref("");
const passwordForm = reactive({ newPassword: "" });
const creditForm = reactive({ credits: 1, reason: "" });

const columns = [
  { title: "用户昵称", key: "nickname", dataIndex: "nickname" },
  { title: "状态", key: "status", width: 110 },
  { title: "剩余额度", key: "generationCredits", dataIndex: "generationCredits", width: 120 },
  { title: "宠物数量", key: "pets", width: 120 },
  { title: "区域", key: "region", dataIndex: "region", width: 90 },
  { title: "操作", key: "actions", width: 320 }
];

const filteredUsers = computed(() => {
  const keyword = query.value.trim().toLowerCase();
  if (!keyword) {
    return users.value;
  }
  return users.value.filter((user) =>
    user.nickname.toLowerCase().includes(keyword) ||
    user.region.toLowerCase().includes(keyword)
  );
});

const enabledCount = computed(() => users.value.filter((user) => !user.isDisabled).length);
const totalCredits = computed(() => users.value.reduce((sum, user) => sum + user.generationCredits, 0));
const totalPets = computed(() => users.value.reduce((sum, user) => sum + user.petCount, 0));

onMounted(loadUsers);

async function loadUsers() {
  loading.value = true;
  try {
    users.value = await listAdminUsers();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "用户列表加载失败。");
  } finally {
    loading.value = false;
  }
}

function openResetPassword(user: AdminUser) {
  passwordUser.value = user;
  passwordForm.newPassword = "";
}

function closeResetPassword() {
  passwordUser.value = null;
  passwordForm.newPassword = "";
}

function openAdjustCredits(user: AdminUser) {
  creditUser.value = user;
  creditForm.credits = 1;
  creditForm.reason = "";
}

function closeAdjustCredits() {
  creditUser.value = null;
  creditForm.credits = 1;
  creditForm.reason = "";
}

async function submitResetPassword() {
  if (!passwordUser.value) {
    return;
  }
  resetting.value = true;
  try {
    const response = await resetAdminUserPassword(passwordUser.value.userId, passwordForm.newPassword);
    replaceUser(response.user);
    message.success(response.message);
    closeResetPassword();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "重置密码失败。");
  } finally {
    resetting.value = false;
  }
}

async function submitAdjustCredits() {
  if (!creditUser.value) {
    return;
  }
  adjustingCredits.value = true;
  try {
    const response = await adjustAdminUserCredits(creditUser.value.userId, Number(creditForm.credits), creditForm.reason);
    replaceUser(response.user);
    if (detailUser.value?.userId === response.user.userId) {
      detailUser.value = response.user;
    }
    message.success(response.message);
    closeAdjustCredits();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "额度调整失败。");
  } finally {
    adjustingCredits.value = false;
  }
}

async function toggleUser(user: AdminUser) {
  togglingUserId.value = user.userId;
  try {
    const response = await setAdminUserStatus(user.userId, !user.isDisabled);
    replaceUser(response.user);
    if (detailUser.value?.userId === response.user.userId) {
      detailUser.value = response.user;
    }
    message.success(response.message);
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "账号状态更新失败。");
  } finally {
    togglingUserId.value = "";
  }
}

function replaceUser(user: AdminUser) {
  users.value = users.value.map((item) => (item.userId === user.userId ? user : item));
}

async function submitDeleteUser(user: AdminUser) {
  deletingUserId.value = user.userId;
  try {
    await deleteAdminUser(user.userId);
    users.value = users.value.filter((item) => item.userId !== user.userId);
    if (detailUser.value?.userId === user.userId) {
      detailUser.value = null;
    }
    if (passwordUser.value?.userId === user.userId) {
      closeResetPassword();
    }
    if (creditUser.value?.userId === user.userId) {
      closeAdjustCredits();
    }
    message.success("账号已移除，历史订单和生成记录会继续保留。");
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "用户删除失败。");
  } finally {
    deletingUserId.value = "";
  }
}
</script>

<template>
  <a-space class="admin-page-stack" direction="vertical" size="large">
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :md="6">
        <a-card><a-statistic title="用户总数" :value="users.length" /></a-card>
      </a-col>
      <a-col :xs="24" :md="6">
        <a-card><a-statistic title="启用账号" :value="enabledCount" /></a-card>
      </a-col>
      <a-col :xs="24" :md="6">
        <a-card><a-statistic title="剩余额度" :value="totalCredits" /></a-card>
      </a-col>
      <a-col :xs="24" :md="6">
        <a-card><a-statistic title="宠物数量" :value="totalPets" /></a-card>
      </a-col>
    </a-row>

    <a-card title="用户列表">
      <div class="admin-filter-bar">
        <a-input
          v-model:value="query"
          class="admin-filter-bar__search"
          allow-clear
          placeholder="搜索用户昵称 / 区域"
        />
      </div>

      <a-table
        row-key="userId"
        :columns="columns"
        :data-source="filteredUsers"
        :loading="loading"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'nickname'">
            <a-button type="link" class="admin-link-button" @click="detailUser = record">
              {{ record.nickname || "未设置" }}
            </a-button>
            <div class="admin-table-subtext">{{ record.userId }}</div>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.isDisabled ? 'red' : 'green'">
              {{ record.isDisabled ? "已禁用" : "已启用" }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'pets'">
            {{ record.petCount }} / {{ record.petLimit }}
          </template>
          <template v-else-if="column.key === 'actions'">
            <a-space wrap>
              <a-button size="small" @click="detailUser = record">查看</a-button>
              <a-button size="small" @click="openResetPassword(record)">重置密码</a-button>
              <a-button size="small" @click="openAdjustCredits(record)">调整额度</a-button>
              <a-button
                size="small"
                :danger="!record.isDisabled"
                :loading="togglingUserId === record.userId"
                @click="toggleUser(record)"
              >
                {{ record.isDisabled ? "启用" : "禁用" }}
              </a-button>
              <a-popconfirm
                title="确定移除这个账号吗？历史订单和生成记录会保留。"
                ok-text="移除"
                cancel-text="取消"
                @confirm="submitDeleteUser(record)"
              >
                <a-button size="small" danger :loading="deletingUserId === record.userId">
                  移除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      title="重置密码"
      :open="Boolean(passwordUser)"
      :confirm-loading="resetting"
      ok-text="确认重置"
      cancel-text="取消"
      @ok="submitResetPassword"
      @cancel="closeResetPassword"
    >
      <a-form layout="vertical" :model="passwordForm">
        <a-alert v-if="passwordUser" type="info" show-icon :message="passwordUser.email || passwordUser.nickname || passwordUser.userId" />
        <a-form-item label="新密码" style="margin-top: 16px">
          <a-input-password v-model:value="passwordForm.newPassword" autocomplete="new-password" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      title="调整生成额度"
      :open="Boolean(creditUser)"
      :confirm-loading="adjustingCredits"
      ok-text="确认调整"
      cancel-text="取消"
      @ok="submitAdjustCredits"
      @cancel="closeAdjustCredits"
    >
      <a-form layout="vertical" :model="creditForm">
        <a-alert
          v-if="creditUser"
          type="info"
          show-icon
          :message="`${creditUser.email || creditUser.nickname || creditUser.userId} 当前剩余额度：${creditUser.generationCredits}`"
        />
        <a-form-item label="调整额度" style="margin-top: 16px">
          <a-input-number v-model:value="creditForm.credits" style="width: 100%" :min="-9999" :max="9999" />
          <div class="admin-table-subtext">正数为补偿额度，负数为人工扣减。</div>
        </a-form-item>
        <a-form-item label="调整原因">
          <a-textarea v-model:value="creditForm.reason" :rows="3" placeholder="例如：生成失败售后补偿" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-drawer
      title="用户详情"
      :open="Boolean(detailUser)"
      width="420"
      @close="detailUser = null"
    >
      <a-descriptions v-if="detailUser" bordered :column="1" size="small">
        <a-descriptions-item label="邮箱">{{ detailUser.email || "未绑定邮箱" }}</a-descriptions-item>
        <a-descriptions-item label="用户昵称">{{ detailUser.nickname || "未设置" }}</a-descriptions-item>
        <a-descriptions-item label="用户 ID">{{ detailUser.userId }}</a-descriptions-item>
        <a-descriptions-item label="区域">{{ detailUser.region }}</a-descriptions-item>
        <a-descriptions-item label="状态">{{ detailUser.isDisabled ? "已禁用" : "已启用" }}</a-descriptions-item>
        <a-descriptions-item label="剩余额度">{{ detailUser.generationCredits }}</a-descriptions-item>
        <a-descriptions-item label="宠物数量">{{ detailUser.petCount }} / {{ detailUser.petLimit }}</a-descriptions-item>
        <a-descriptions-item label="最近登录">{{ detailUser.lastLoginAt ? new Date(detailUser.lastLoginAt).toLocaleString() : "未记录" }}</a-descriptions-item>
      </a-descriptions>
      <template #footer>
        <a-space v-if="detailUser">
          <a-button @click="openResetPassword(detailUser)">重置密码</a-button>
          <a-button @click="openAdjustCredits(detailUser)">调整额度</a-button>
          <a-button
            :danger="!detailUser.isDisabled"
            :loading="togglingUserId === detailUser.userId"
            @click="toggleUser(detailUser)"
          >
            {{ detailUser.isDisabled ? "启用账号" : "禁用账号" }}
          </a-button>
          <a-popconfirm
            title="确定移除这个账号吗？历史订单和生成记录会保留。"
            ok-text="移除"
            cancel-text="取消"
            @confirm="submitDeleteUser(detailUser)"
          >
            <a-button danger :loading="deletingUserId === detailUser.userId">移除账号</a-button>
          </a-popconfirm>
        </a-space>
      </template>
    </a-drawer>
  </a-space>
</template>
